import { DocumentTemplateShell } from './TemplateShared';
import {
  TemplateAppendixLayout,
  TemplateSummaryHeader,
} from './TemplateAppendixLayout';
import { SignedUrlPreview } from './SignedUrlPreview';
import { isExpandableSignedUrlPreview } from './signedUrlPreviewUtils';
import { DASH, EMPTY } from './templateUtils';
import type { TemplatePatient } from './templateUtils';

export interface ReferenceDocumentSection {
  title?: string;
  rows: Array<{ label?: string; value?: string }>;
}

export interface ReferenceDocumentEntry {
  id?: string;
  documentId?: string;
  reference?: string;
  encounter?: string;
  contentType?: string;
  typeCodingSystem?: string;
  typeCoding?: string;
  signedUrl?: string;
  inlineText?: string;
  sections: ReferenceDocumentSection[];
}

export interface ReferenceDocumentsTemplateProps {
  entries: ReferenceDocumentEntry[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightEntryId?: string;
}

const summaryHeaders = [
  'Document ID',
  'Reference',
  'Encounter',
  'Content Type',
  'Page',
] as const;

const NO_INFO = 'No information provided';

const SummaryTable = ({
  entries,
  highlightEntryId,
}: {
  entries: ReferenceDocumentEntry[];
  highlightEntryId?: string;
}) => (
  <div className="overflow-hidden rounded-sm border border-zinc-200">
    <div className="flex bg-slate-100">
      {summaryHeaders.map((h, idx) => (
        <div
          key={h}
          className={`flex flex-1 px-3 py-2.5 ${
            idx < summaryHeaders.length - 1 ? 'border-r border-zinc-300' : ''
          }`}
        >
          <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
            {h}
          </p>
        </div>
      ))}
    </div>
    {entries.map((entry, idx) => {
      const isHighlighted = !!highlightEntryId && entry.id === highlightEntryId;
      const zebra = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
      const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
      return (
        <div
          key={entry.id}
          className={`flex border-t border-zinc-200 ${rowBg}`}
        >
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.documentId?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.reference?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.encounter?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.contentType?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 px-3 py-2.5">
            <a
              href={`#reference-document-${entry.id}`}
              className="flex-1 text-[10px] leading-tight text-blue-600"
            >
              {idx + 2}
            </a>
          </div>
        </div>
      );
    })}
  </div>
);

const SectionTable = ({ section }: { section: ReferenceDocumentSection }) => (
  <div className="flex flex-col gap-2">
    {section.title ? (
      <p className="text-[11px] leading-tight font-bold text-zinc-700">
        {section.title}
      </p>
    ) : null}
    <div className="overflow-hidden rounded-sm border border-zinc-200">
      <div className="flex bg-slate-100">
        <div className="flex flex-1 border-r border-zinc-300 px-3 py-2">
          <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
            Field
          </p>
        </div>
        <div className="flex flex-1 px-3 py-2">
          <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
            Value
          </p>
        </div>
      </div>
      {section.rows.map((row, idx) => (
        <div
          key={`${section.title}-row-${idx}`}
          className={`flex border-t border-zinc-200 ${
            idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'
          }`}
        >
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {row.label?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 px-3 py-2">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {row.value?.trim() || DASH}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex flex-col gap-1">
    <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
      {label}
    </p>
    <p className="text-[11px] leading-tight whitespace-pre-line text-zinc-950">
      {value?.trim() || DASH}
    </p>
  </div>
);

const AppendixCard = ({
  entry,
  highlightEntryId,
}: {
  entry: ReferenceDocumentEntry;
  highlightEntryId?: string;
}) => {
  const isHighlighted = !!highlightEntryId && entry.id === highlightEntryId;
  const expandsPreview = isExpandableSignedUrlPreview(entry.contentType);
  return (
    <article
      id={`reference-document-${entry.id}`}
      className={`flex flex-col overflow-hidden rounded-sm border-2 ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      } bg-white pb-4`}
    >
      <header className="flex flex-col gap-1.5 bg-slate-700 px-5 pt-5 pb-3 text-white">
        <p className="text-[14px] leading-tight font-semibold capitalize">
          {entry.reference?.trim() || EMPTY}
        </p>
        <p className="text-[10px] leading-tight">
          <span className="font-semibold">Document Reference ID:</span>{' '}
          {entry.documentId?.trim() || EMPTY}
        </p>
      </header>

      <div className="mx-5 mt-3 grid grid-cols-2 gap-4 rounded-sm border border-zinc-200 bg-white px-4 py-3">
        <InfoRow label="Content Type" value={entry.contentType} />
        <InfoRow label="Type Coding System" value={entry.typeCodingSystem} />
        <InfoRow label="Encounter" value={entry.encounter} />
        <InfoRow label="Type Coding" value={entry.typeCoding} />
      </div>

      {entry.sections.length === 0 && !entry.inlineText && !entry.signedUrl ? (
        <div className="mx-5 mt-3 rounded-sm border border-zinc-200 bg-slate-50 px-4 py-3 text-[10px] leading-tight text-zinc-500 italic">
          {NO_INFO}
        </div>
      ) : null}

      {entry.signedUrl ? (
        <div
          className={`mx-5 mt-3 flex rounded-sm border border-zinc-200 ${
            expandsPreview
              ? 'items-stretch justify-start bg-white'
              : 'h-48 items-center justify-center overflow-hidden bg-zinc-100'
          }`}
        >
          <SignedUrlPreview
            signedUrl={entry.signedUrl}
            contentType={entry.contentType}
            title={`Reference document ${entry.documentId ?? entry.id} preview`}
          />
        </div>
      ) : null}

      {entry.inlineText ? (
        <div className="mx-5 mt-3 rounded-sm border border-zinc-200 bg-slate-50 px-4 py-3">
          <p className="text-[11px] leading-tight whitespace-pre-line text-zinc-950">
            {entry.inlineText}
          </p>
        </div>
      ) : null}

      {entry.sections.length > 0 ? (
        <div className="mx-5 mt-3 flex flex-col gap-3">
          {entry.sections.map((section, idx) => (
            <SectionTable
              key={`${entry.id}-section-${idx}`}
              section={section}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
};

export const ReferenceDocumentsTemplate = ({
  entries,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightEntryId,
}: ReferenceDocumentsTemplateProps) => {
  const items = entries ?? [];
  const hasItems = items.length > 0;

  return (
    <DocumentTemplateShell
      title="Document References Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <TemplateAppendixLayout
        hasItems={hasItems}
        emptyMessage="No document references available"
        appendixTitle=""
        summaryHeader={
          <TemplateSummaryHeader
            title="All Document References"
            note="*Select any row to jump to the document"
          />
        }
        summaryTable={
          <SummaryTable entries={items} highlightEntryId={highlightEntryId} />
        }
        appendix={items.map((entry) => (
          <AppendixCard
            key={entry.id}
            entry={entry}
            highlightEntryId={highlightEntryId}
          />
        ))}
      />
    </DocumentTemplateShell>
  );
};
